<template>
  <Loading v-if="componentLoading" :style="style" />
  <Chart v-else-if="visible" class="compare-line-chart" ref="chartComponent" :option="chartConfig" :deepUpdate="true" :style="style"></Chart>
</template>

<script lang="ts" setup>
import Chart from "@p-v/packages/Charts/BaseChart/index.vue"
import Loading from "@p-v/packages/Loading.vue"
import { ref } from "vue"
import { cloneDeep, defaultsDeep } from "lodash-es"
import config from "./config"
import useToggle from "@p-v/hooks/useToggle"
import type { DataItem, VerticalBarChartProps } from "./index.d"
import { EChartsOption, XAXisComponentOption, YAXisComponentOption } from "echarts"

const componentLoading = ref<boolean>(true)
const chartComponent = ref<InstanceType<typeof Chart>>()

const props = withDefaults(defineProps<VerticalBarChartProps>(), {
  config: () => ({}),
  style: () => ({}),
  show: () => true,
})

const { visible, show, hide, changeVisible } = useToggle(props.show)

const chartConfig = ref(cloneDeep(config))

/**
 * 数据处理
 * @param config
 * @param dataList
 */
function componentPropEngine(propsData: VerticalBarChartProps): EChartsOption {
  const { data, config: _config } = propsData
  const newConfig = cloneDeep(config)
  if (!data) return newConfig
  const configFromProp = cloneDeep(_config)
  const configResult = defaultsDeep(configFromProp, props.config, newConfig)
  let hasData = false
  if (Array.isArray(data)) {
    const serieTpl = cloneDeep(configResult.series[0])
    const yAxisTpl = cloneDeep(configResult.yAxis)
    const xAxisTpl = cloneDeep(configResult.xAxis)
    let yAxis = [] as YAXisComponentOption[]
    let xAxis = [] as XAXisComponentOption[]
    configResult.series = data.map(item => {
      const { name, data, unit, axis, config, showXAxis, showYAxis } = item
      if (yAxis.length < 2) {
        let y: YAXisComponentOption
        if (Array.isArray(yAxisTpl)) {
          if (yAxisTpl.length === 1) {
            y = yAxisTpl[0]
          } else {
            y = yAxisTpl[yAxis.length]
          }
        } else {
          y = yAxisTpl
        }
        if (yAxis.length === 0) {
          yAxis.push(defaultsDeep({ name: unit, show: true }, y))
        } else if (showYAxis) {
          yAxis.push(defaultsDeep({ name: unit, show: true }, y))
        }
      }
      if (xAxis.length < 2) {
        let x: XAXisComponentOption
        if (Array.isArray(xAxisTpl)) {
          if (xAxisTpl.length === 1) {
            x = xAxisTpl[0]
          } else {
            x = xAxisTpl[xAxis.length]
          }
        } else {
          x = xAxisTpl
        }
        if (xAxis.length === 0) {
          xAxis.push(defaultsDeep({ data: axis, show: true }, x))
        } else if (showXAxis) {
          xAxis.push(defaultsDeep({ data: axis, show: true }, x))
        }
      }
      return defaultsDeep({ name, data, ...config }, serieTpl)
    })
    hasData = data.some(item => item.data.length > 0)
    if (hasData) {
      configResult.yAxis = yAxis
      configResult.xAxis = xAxis
    }
  } else {
    configResult.series[0].name = data.name
    configResult.series[0].data = data.data
    configResult.yAxis.name = data.unit
    configResult.xAxis.data = data.axis
    hasData = data.data.length > 0
  }

  if (hasData) {
    // 移除暂无数据的文本
    // delete configResult.graphic;
    configResult.series.forEach((item, index) => {
      if (item.type != "bar") {
        delete item.barWidth
      }
    })
    configResult.graphic[0].invisible = true
  } else {
    configResult.legend.show = false
    configResult.xAxis.data = []
    configResult.xAxis.show = false
    configResult.yAxis.show = false
    configResult.series = []
    configResult.graphic[0].invisible = false
  }

  // console.log("configResult", configResult)

  return configResult
}
const loading = () => {
  componentLoading.value = true
}
const setData = (data: { data: DataItem | DataItem[]; config?: EChartsOption }) => {
  const result = componentPropEngine(data)
  chartConfig.value = result
  componentLoading.value = false
}
defineExpose({ setData, loading, show, hide, changeVisible })
</script>

<style lang="scss">
.compare-line-chart {
  height: 100%;
}
</style>
.
